import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  notificationItems,
  notificationTypes,
  notificationCategories,
  notificationDashboardStats,
  notificationActions,
  getUnreadNotifications,
  getNotificationsByCategory,
  getNotificationsByType,
  getPinnedNotifications,
  getTypeById,
  getCategoryById,
  type NotificationItem,
  type NotificationCategoryId,
  type NotificationTypeId,
} from '@/mocks/rfwNotificationCenter';

function NotifDetailCard({ item, onMarkRead, onDelete }: { item: NotificationItem; onMarkRead: (id: number) => void; onDelete: (id: number) => void }) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = getTypeById(item.type);
  const categoryInfo = getCategoryById(item.category);

  return (
    <div className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
      item.isRead
        ? 'bg-background-50 border-background-200/70 hover:border-foreground-300/50'
        : 'bg-background-50 border-accent-500/30 ring-1 ring-accent-100'
    }`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${typeInfo ? typeInfo.color.replace('text-', 'bg-').replace('600', '100') : 'bg-background-100'}`}>
              <i className={`${typeInfo?.icon || 'ri-notification-3-line'} ${typeInfo?.color || 'text-foreground-600'} text-lg`}></i>
            </div>
            {!item.isRead && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-accent-500 border-2 border-white"></span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-950">{item.title}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {typeInfo && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeInfo.color.replace('text-', 'bg-').replace('600', '100')} ${typeInfo.color}`}>
                      <i className={`${typeInfo.icon} text-[9px]`}></i>
                      {typeInfo.name}
                    </span>
                  )}
                  {categoryInfo && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-foreground-400">
                      <i className={`${categoryInfo.icon} text-[9px]`}></i>
                      {categoryInfo.name}
                    </span>
                  )}
                  {item.isPinned && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 text-[9px] font-semibold">
                      <i className="ri-pushpin-line text-[8px]"></i>
                      Fijado
                    </span>
                  )}
                  {item.priority === 'urgente' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-semibold">
                      <i className="ri-alert-line text-[8px]"></i>
                      Urgente
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[10px] text-foreground-400 whitespace-nowrap shrink-0">{item.timestampRelative}</span>
            </div>

            <div className="flex items-center gap-2 mt-2 text-[10px] text-foreground-400">
              <img src={item.iconUrl} alt={item.sourceName} className="w-4 h-4 rounded-full object-cover object-top" />
              <span className="font-medium text-foreground-500">{item.sourceName}</span>
              {item.sourceFreq && <span>· {item.sourceFreq}</span>}
              <span className="text-foreground-300">· {item.metadata}</span>
            </div>

            {expanded && (
              <div className="mt-3 pt-3 border-t border-background-100">
                <div className="relative rounded-xl overflow-hidden mb-3 h-36">
                  <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.message}</p>
              </div>
            )}

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button
                onClick={() => setExpanded(!expanded)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[9px]`}></i>
                {expanded ? 'Menos' : 'Más detalles'}
              </button>
              <Link
                to={item.actionUrl}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-[10px] font-semibold transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-eye-line text-[9px]"></i>
                {item.actionLabel}
              </Link>
              {!item.isRead && item.canMarkRead && (
                <button
                  onClick={() => onMarkRead(item.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-check-double-line text-[9px]"></i>
                  Marcar leído
                </button>
              )}
              {item.canDelete && (
                <button
                  onClick={() => onDelete(item.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-600 text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-[9px]"></i>
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CentroNotificacionesPage() {
  const [activeView, setActiveView] = useState<'todas' | 'no-leidas' | 'categorias'>('todas');
  const [activeCategory, setActiveCategory] = useState<NotificationCategoryId | null>(null);
  const [activeType, setActiveType] = useState<NotificationTypeId | null>(null);
  const [items, setItems] = useState(notificationItems);

  const unreadCount = getUnreadNotifications().length;
  const pinnedItems = getPinnedNotifications();

  const filteredItems = useMemo(() => {
    let result = items;

    if (activeView === 'no-leidas') {
      result = result.filter((n) => !n.isRead);
    }

    if (activeCategory) {
      result = result.filter((n) => n.category === activeCategory);
    }

    if (activeType) {
      result = result.filter((n) => n.type === activeType);
    }

    return result;
  }, [items, activeView, activeCategory, activeType]);

  const handleMarkRead = (id: number) => {
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
  };

  const handleDelete = (id: number) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  const handleMarkAllRead = () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const views = [
    { id: 'todas' as const, label: 'Todas', icon: 'ri-notification-3-line', count: items.length },
    { id: 'no-leidas' as const, label: 'No Leídas', icon: 'ri-mail-unread-line', count: unreadCount },
    { id: 'categorias' as const, label: 'Por Categoría', icon: 'ri-price-tag-3-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-accent-500 via-rose-500 to-orange-500 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)' }}></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/" className="text-white/60 hover:text-white text-xs transition-colors duration-200">
              RFW
            </Link>
            <i className="ri-arrow-right-s-line text-white/40 text-xs"></i>
            <span className="text-white/80 text-xs font-medium">Centro de Notificaciones</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-3">
            Centro de <span className="text-accent-200">Notificaciones</span>
          </h1>
          <p className="text-white/70 max-w-xl text-sm md:text-base mb-6">
            Todas tus notificaciones en un solo lugar. Ver, marcar leído, eliminar o configurar. Cada notificación te lleva directo a lo que importa, sin vueltas.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/notifications" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-all duration-200 whitespace-nowrap backdrop-blur-sm">
              <i className="ri-settings-3-line text-[10px]"></i>
              Configurar Preferencias
            </Link>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-all duration-200 whitespace-nowrap backdrop-blur-sm cursor-pointer">
                <i className="ri-check-double-line text-[10px]"></i>
                Marcar Todas Leídas
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="grid grid-cols-2 md:grid-cols-8 gap-3">
            {[
              { label: 'Total', value: (notificationDashboardStats.totalNotificaciones / 1000).toFixed(1) + 'K', icon: 'ri-notification-3-line', color: 'text-accent-600' },
              { label: 'Hoy', value: notificationDashboardStats.totalHoy, icon: 'ri-calendar-line', color: 'text-sky-600' },
              { label: 'Sin Leer', value: unreadCount, icon: 'ri-mail-unread-line', color: 'text-rose-600' },
              { label: 'Apertura', value: notificationDashboardStats.tasaApertura + '%', icon: 'ri-eye-line', color: 'text-emerald-600' },
              { label: 'T. Respuesta', value: notificationDashboardStats.tiempoPromedioAccion, icon: 'ri-timer-line', color: 'text-amber-600' },
              { label: 'Por Día', value: notificationDashboardStats.notificacionesPorDia, icon: 'ri-bar-chart-line', color: 'text-violet-600' },
              { label: 'Más Free.', value: notificationDashboardStats.masFrecuente, icon: 'ri-trophy-line', color: 'text-orange-600', sub: notificationDashboardStats.masFrecuenteCount.toLocaleString() },
              { label: 'Fijadas', value: pinnedItems.length, icon: 'ri-pushpin-line', color: 'text-indigo-600' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-lg ${stat.color.replace('text-', 'bg-').replace('600', '50')} flex items-center justify-center shrink-0`}>
                  <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-[9px] text-foreground-500">{stat.label}</p>
                  {stat.sub && <p className="text-[8px] text-foreground-400">{stat.sub}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Views tabs + filters */}
      <div className="bg-white border-b border-background-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {views.map((view) => (
                <button
                  key={view.id}
                  onClick={() => { setActiveView(view.id); setActiveCategory(null); setActiveType(null); }}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === view.id && !activeCategory && !activeType
                      ? 'bg-accent-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${view.icon} text-[10px]`}></i>
                  {view.label}
                  {view.count !== undefined && (
                    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[9px] font-bold px-1 ${
                      activeView === view.id && !activeCategory && !activeType ? 'bg-white/20 text-white' : 'bg-accent-100 text-accent-600'
                    }`}>
                      {view.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <span className="w-px h-6 bg-background-200 hidden md:block"></span>

            <div className="flex items-center gap-1.5 flex-wrap">
              {notificationCategories.slice(0, 6).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(activeCategory === cat.id ? null : cat.id);
                    setActiveType(null);
                    setActiveView('todas');
                  }}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? `${cat.badgeColor} text-white`
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-[9px]`}></i>
                  {cat.name}
                  <span className="text-[9px] opacity-70">{cat.unreadCount}</span>
                </button>
              ))}
            </div>

            <span className="w-px h-6 bg-background-200 hidden md:block"></span>

            <div className="flex items-center gap-1 flex-wrap">
              {notificationTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => {
                    setActiveType(activeType === type.id ? null : type.id);
                    setActiveCategory(null);
                    setActiveView('todas');
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeType === type.id
                      ? `${type.color.replace('text-', 'bg-').replace('600', '500')} text-white`
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${type.icon} text-[9px]`}></i>
                  {type.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notifications list */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-accent-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-3xl text-accent-400"></i>
            </div>
            <p className="text-base font-semibold text-foreground-950 mb-1">No hay notificaciones</p>
            <p className="text-xs text-foreground-500">Probá cambiando los filtros para ver más resultados.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <NotifDetailCard
                key={item.id}
                item={item}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Stats + Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          <div className="md:col-span-2 p-5 rounded-2xl bg-background-50 border border-background-200/70">
            <h3 className="text-sm font-bold text-foreground-950 mb-4">Distribución por Categoría</h3>
            <div className="space-y-3">
              {notificationCategories.slice(0, 5).map((cat) => {
                const pct = (cat.totalCount / notificationDashboardStats.totalNotificaciones) * 100;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-md ${cat.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                          <i className={`${cat.icon} ${cat.color} text-[10px]`}></i>
                        </div>
                        <span className="font-medium text-foreground-700">{cat.name}</span>
                      </div>
                      <span className="text-foreground-500">{cat.totalCount.toLocaleString()} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${cat.badgeColor}`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-accent-50 border border-accent-200/50">
            <h3 className="text-sm font-bold text-foreground-950 mb-4">Acciones Rápidas</h3>
            <div className="space-y-2">
              {notificationActions.map((action) => (
                <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-background-200/70">
                  <div className={`w-9 h-9 rounded-xl ${action.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center shrink-0`}>
                    <i className={`${action.icon} ${action.color} text-base`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-950">{action.name}</p>
                    <p className="text-[9px] text-foreground-500">{action.description}</p>
                    <p className="text-[8px] text-foreground-400 mt-0.5">Atajo: {action.shortcut}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cross-links */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-10 pt-8 border-t border-background-100">
          <span className="text-xs text-foreground-400">Explorar más:</span>
          {[
            { to: '/actividad', label: 'Actividad', icon: 'ri-flashlight-line' },
            { to: '/seguidores', label: 'Seguidores', icon: 'ri-user-add-line' },
            { to: '/clubes', label: 'Clubes', icon: 'ri-group-line' },
            { to: '/eventos', label: 'Eventos', icon: 'ri-calendar-event-line' },
            { to: '/competencias', label: 'Concursos', icon: 'ri-trophy-line' },
            { to: '/configuracion', label: 'Preferencias', icon: 'ri-settings-3-line' },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-medium transition-all duration-200 whitespace-nowrap"
            >
              <i className={`${link.icon} text-[10px]`}></i>
              {link.label}
            </Link>
          ))}
        </div>

        {/* Regla final */}
        <div className="mt-8 p-6 rounded-2xl bg-accent-100/60 border border-accent-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-flashlight-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Las notificaciones deben llevar directamente a la acción.
          </p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            Cada notificación es un puente directo. Radio en vivo, un toque al play. Nuevo concurso, un toque a participar. Comentario nuevo, un toque a responder. El Centro de Notificaciones existe para que nunca pierdas tiempo buscando — solo actuando.
          </p>
        </div>
      </div>
    </div>
  );
}